// Turning a runner's raw events into the detail page's activity lines and
// export groups.

import type { AdminEvent, AdminRunnerDetail } from "@paceroo/domain/views/admin";
import { FORMAT_LABEL, FORMAT_TAG, methodLabel, parseWhen } from "../lib/format";

export type ActivityGroup = "all" | "auth" | "plan" | "export" | "mcp";

export interface ActivityLine {
  at: Date;
  event: string;
  group: Exclude<ActivityGroup, "all">;
  text: string;
  bad: boolean;
}

/** Heartbeats are counted, not listed. */
const HIDDEN = new Set(["runner.active"]);

export function activityLines(d: AdminRunnerDetail): ActivityLine[] {
  const planByTemplate = new Map(d.plans.filter((p) => p.templateId).map((p) => [p.templateId!, p.name]));
  const planById = new Map(d.plans.map((p) => [p.id, p.name]));
  const client = (id: string | undefined) => d.assistants.find((a) => a.clientId === id)?.name ?? "Assistant";
  return (d.activity ?? [])
    .filter((e) => !HIDDEN.has(e.event))
    .map((e) => ({ at: parseWhen(e.at), event: e.event, group: groupOf(e.event), text: describe(e), bad: e.fields["outcome"] === "error" || e.fields["outcome"] === "exception" }));

  function describe(e: AdminEvent): string {
    const f = e.fields;
    switch (e.event) {
      case "auth.sign_up":
        return `Signed up with ${methodLabel(f["method"] ?? "password")}`;
      case "auth.account_linked":
        return `Linked ${methodLabel(f["method"] ?? "")} sign-in`;
      case "auth.email_verified":
        return "Verified email";
      case "auth.sign_in":
        return "Signed in";
      case "plan.first_started":
        return "Started their first plan";
      case "plan.started": {
        const name = planByTemplate.get(f["templateId"] ?? "");
        return `Started ${name ? `“${name}”` : `a ${f["weeks"] ?? "?"}-week ${f["source"] ?? ""} plan`}${f["via"] === "mcp" ? " · via assistant" : ""}${f["trimmed"] === "true" ? " · trimmed" : ""}`;
      }
      case "plan.edited":
        return `Edited plan · ${(f["kind"] ?? "change").replace(/_/g, " ")}${f["via"] === "mcp" ? " · via assistant" : ""}`;
      case "plan.archived":
        return "Archived a plan";
      case "plan.restored":
        return "Restored a plan";
      case "plan.deleted":
        return "Deleted a plan";
      case "export.downloaded": {
        const plan = planById.get(f["planId"] ?? "");
        return `${FORMAT_LABEL[f["format"] ?? ""] ?? f["format"]}${plan ? ` · ${plan}` : ""} · ${f["via"] === "assistant_link" ? "signed link (assistant)" : "in app"}`;
      }
      case "mcp.connected":
        return `Connected ${f["clientName"] || "an assistant"} · ${f["access"] === "read_write" ? "read and write" : "read only"}`;
      case "mcp.disconnected":
        return `Disconnected ${client(f["clientId"])}${f["via"] ? ` · ${f["via"]}` : ""}`;
      case "mcp.call":
        return `${client(f["clientId"])} · ${f["tool"]} · ${f["outcome"]}${e.durationMs ? ` · ${e.durationMs < 1000 ? `${e.durationMs} ms` : `${(e.durationMs / 1000).toFixed(1)} s`}` : ""}`;
      case "mcp.rate_limited":
        return `${client(f["clientId"])} was rate-limited${f["limit"] ? ` (limit ${f["limit"]})` : ""}`;
      default:
        return Object.entries(f)
          .map(([k, v]) => `${k}: ${v}`)
          .join(" · ");
    }
  }
}

function groupOf(event: string): ActivityLine["group"] {
  if (event.startsWith("plan.")) return "plan";
  if (event.startsWith("export.")) return "export";
  if (event.startsWith("mcp.")) return "mcp";
  return "auth";
}

export interface ExportGroup {
  start: Date;
  end: Date;
  plan: string;
  viaLink: boolean;
  pips: Array<{ tag: string; count: number }>;
  items: Array<{ at: Date; format: string; viaLink: boolean }>;
  /** Two or more formats, or three or more downloads. */
  flag: boolean;
}

/** Downloads of the same plan within 30 minutes of each other form one group. Newest group first. */
export function exportGroups(d: AdminRunnerDetail): ExportGroup[] {
  const planById = new Map(d.plans.map((p) => [p.id, p.name]));
  const downloads = (d.activity ?? [])
    .filter((e) => e.event === "export.downloaded")
    .map((e) => ({ at: parseWhen(e.at), planId: e.fields["planId"] ?? "", format: e.fields["format"] ?? "?", viaLink: e.fields["via"] === "assistant_link" }))
    .sort((a, b) => a.at.getTime() - b.at.getTime());
  const groups: Array<{ planId: string; start: Date; end: Date; items: typeof downloads }> = [];
  for (const dl of downloads) {
    const g = groups[groups.length - 1];
    if (g && g.planId === dl.planId && dl.at.getTime() - g.end.getTime() <= 30 * 60_000) {
      g.items.push(dl);
      g.end = dl.at;
    } else groups.push({ planId: dl.planId, start: dl.at, end: dl.at, items: [dl] });
  }
  return groups.reverse().map((g) => {
    const counts = new Map<string, number>();
    for (const i of g.items) counts.set(i.format, (counts.get(i.format) ?? 0) + 1);
    return {
      start: g.start,
      end: g.end,
      plan: planById.get(g.planId) ?? (g.planId ? "A plan no longer on the account" : "A workout"),
      viaLink: g.items.some((i) => i.viaLink),
      pips: [...counts].map(([f, count]) => ({ tag: FORMAT_TAG[f] ?? f.toUpperCase(), count })),
      items: g.items.map((i) => ({ at: i.at, format: FORMAT_LABEL[i.format] ?? i.format, viaLink: i.viaLink })),
      flag: counts.size >= 2 || g.items.length >= 3,
    };
  });
}
